// src/features/live/LiveChatPanel.tsx — the live chat panel beside the player
// (web). Seeded messages + a visibly-disabled composer. A chat box that accepts
// typing and silently discards it is exactly the inert-control pattern this
// batch keeps fixing (slice-4-xl §2), so the composer is wrapped in DisabledHint
// (block mode — it must fill the full width, not shrink-wrap like a button).
import styled from 'styled-components';
import { Avatar, DisabledHint, ConsolePanel } from '@/ui';
import { Icon } from '@/icons';
import type { LiveChatMessage } from '@/core/models/live';

const Panel = styled(ConsolePanel).attrs({ $surfaceRole: 'panel' })`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 320px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  padding: 0;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const MessageList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 10px 14px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MessageRow = styled.li`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const MessageText = styled.p`
  margin: 0;
  min-width: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const AuthorName = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.color.accentCyan};
`;

const ComposerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const ComposerInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: not-allowed;
`;

const SendButton = styled.button`
  display: inline-grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surfaceRaised};
  color: ${({ theme }) => theme.color.textMuted};
  cursor: not-allowed;

  /* Explicit muting on the disabled state (slice-4-xl fix round S1), the
     same aria-disabled selector ChromeButton uses, so "greyed" isn't just an
     accident of theme.color.textMuted already being dim. */
  &[aria-disabled='true'] {
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
  }
`;

export interface LiveChatPanelProps {
  messages: LiveChatMessage[];
}

export function LiveChatPanel({ messages }: LiveChatPanelProps) {
  return (
    <Panel>
      <Header>Live chat</Header>
      <MessageList>
        {messages.map((m) => (
          <MessageRow key={m.id}>
            <Avatar name={m.authorName} size={24} />
            <MessageText>
              <AuthorName>{m.authorName}</AuthorName> {m.body}
            </MessageText>
          </MessageRow>
        ))}
      </MessageList>
      <DisabledHint block explanation="Live chat isn't available yet. This is a placeholder.">
        {(describedBy) => (
          <ComposerRow>
            <ComposerInput
              type="text"
              placeholder="Say something..."
              aria-label="Chat message"
              aria-disabled="true"
              aria-describedby={describedBy}
              readOnly
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
            <SendButton
              type="button"
              aria-disabled="true"
              aria-describedby={describedBy}
              aria-label="Send (unavailable)"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Icon name="send" size={16} />
            </SendButton>
          </ComposerRow>
        )}
      </DisabledHint>
    </Panel>
  );
}
